"use client";

import { useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { RxChevronLeft, RxChevronRight } from "react-icons/rx";

import type { ReviewCard } from "@/components/Testimonials";

type Props = {
  reviews: ReviewCard[];
};

const NAV_BTN =
  "flex size-10 shrink-0 items-center justify-center rounded-full border border-white/40 bg-transparent text-white transition-colors active:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50 md:size-11 md:hover:bg-white/10";

/**
 * Jedna opinia na środku — Embla.
 * Mobile: strzałki w rzędzie z autorem.
 * Desktop: strzałki po bokach cytatu.
 */
export function Testimonial10({ reviews }: Props) {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: "start",
    containScroll: "trimSnaps",
  });
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    if (!emblaApi) return;

    const onSelect = () => {
      setSelectedIndex(emblaApi.selectedScrollSnap());
    };

    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
    onSelect();

    return () => {
      emblaApi.off("select", onSelect);
      emblaApi.off("reInit", onSelect);
    };
  }, [emblaApi]);

  if (reviews.length === 0) return null;

  const showNav = reviews.length > 1;
  const activeName = reviews[selectedIndex]?.name ?? reviews[0]?.name ?? "";

  return (
    <div
      className="mx-auto w-full max-w-3xl"
      role="region"
      aria-roledescription="carousel"
      aria-label="Opinie klientów"
    >
      <div className="relative md:px-16">
        <div className="overflow-hidden" ref={emblaRef}>
          <div className="flex touch-pan-y items-stretch">
            {reviews.map((review, index) => (
              <div
                key={review.id ?? `${review.name}-${index}`}
                className="flex min-w-0 shrink-0 grow-0 basis-full"
                role="group"
                aria-roledescription="slide"
                aria-label={`${index + 1} z ${reviews.length}`}
              >
                <div className="mx-auto flex h-full w-full max-w-xl flex-col items-center justify-center text-center text-white">
                  <blockquote
                    data-no-reveal
                    className="text-base leading-relaxed md:text-[20px]"
                  >
                    {review.quote}
                  </blockquote>
                  {/* Desktop: autor pod cytatem; mobile w rzędzie ze strzałkami */}
                  <p
                    data-no-reveal
                    className="mt-6 hidden text-sm uppercase tracking-wider text-white/80 md:block"
                  >
                    {review.name}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Mobile: strzałki na wysokości autora */}
        <div className="mt-5 flex items-center justify-between gap-3 md:hidden">
          {showNav ? (
            <button
              type="button"
              onClick={() => emblaApi?.scrollPrev()}
              className={NAV_BTN}
              aria-label="Poprzednia opinia"
            >
              <RxChevronLeft className="size-5" aria-hidden />
            </button>
          ) : (
            <span className="size-10 shrink-0" aria-hidden />
          )}
          <p
            data-no-reveal
            className="min-w-0 text-center text-sm uppercase tracking-wider text-white/80"
          >
            {activeName}
          </p>
          {showNav ? (
            <button
              type="button"
              onClick={() => emblaApi?.scrollNext()}
              className={NAV_BTN}
              aria-label="Następna opinia"
            >
              <RxChevronRight className="size-5" aria-hidden />
            </button>
          ) : (
            <span className="size-10 shrink-0" aria-hidden />
          )}
        </div>

        {showNav ? (
          <>
            <button
              type="button"
              onClick={() => emblaApi?.scrollPrev()}
              className={`${NAV_BTN} absolute top-[42%] left-0 z-10 hidden -translate-y-1/2 md:flex`}
              aria-label="Poprzednia opinia"
            >
              <RxChevronLeft className="size-6" aria-hidden />
            </button>
            <button
              type="button"
              onClick={() => emblaApi?.scrollNext()}
              className={`${NAV_BTN} absolute top-[42%] right-0 z-10 hidden -translate-y-1/2 md:flex`}
              aria-label="Następna opinia"
            >
              <RxChevronRight className="size-6" aria-hidden />
            </button>
          </>
        ) : null}
      </div>
    </div>
  );
}
